import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { truncateText, type SmsContent } from "@forgepoint/shared";
import { BodyRenderer } from "./BodyRenderer";

const PREVIEW_LENGTH = 150;

/**
 * Displays expandable SMS content inline for activity events.
 * Shows from, to, and body with expandable content.
 * @param smsContent - The SMS content to display
 */
export const SMSViewer = ({ smsContent }: { smsContent: SmsContent }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const { from, to, body } = smsContent;
  const hasBody = body && body.length > 0;
  const bodyPreview = hasBody ? truncateText(body, PREVIEW_LENGTH) : null;
  const hasLongBody = hasBody && body.length > PREVIEW_LENGTH;

  return (
    <div className="space-y-2">
      <div className="border rounded-md p-3 bg-muted/30 space-y-2 text-xs">
        {from && (
          <div>
            <span className="font-semibold">From:</span>{" "}
            <span className="text-muted-foreground">{from}</span>
          </div>
        )}
        {to && (
          <div>
            <span className="font-semibold">To:</span>{" "}
            <span className="text-muted-foreground">{to}</span>
          </div>
        )}
        {hasBody && (
          <div className={isExpanded ? "pt-2 border-t" : ""}>
            <BodyRenderer body={isExpanded ? body : (bodyPreview ?? "")} />
          </div>
        )}
        {hasLongBody && (
          <div className={isExpanded ? "pt-2 border-t" : ""}>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs p-0 hover:bg-transparent"
              onClick={() => setIsExpanded((current) => !current)}
            >
              {isExpanded ? (
                <ChevronDown className="h-3 w-3 mr-1" />
              ) : (
                <ChevronRight className="h-3 w-3 mr-1" />
              )}
              {isExpanded ? "Hide SMS" : "View full SMS"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
